import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, Loader2 } from "lucide-react";
import BurnTab from "@/components/advisory/BurnTab";
import TaxTab from "@/components/advisory/TaxTab";
import DebtTab from "@/components/advisory/DebtTab";
import ReportTab from "@/components/advisory/ReportTab";
import GoalsPanel from "@/components/advisory/GoalsPanel";
import { TrainingVideoButton } from "@/components/advisory/TrainingVideo";
import ShareVideoButton from "@/components/advisory/ShareVideoButton";
import { useSearchParams } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const TABS = [
  ["burn", "Cash Burn & Runway"],
  ["tax", "Tax Savings"],
  ["debt", "Debt Reduction"],
  ["goals", "Client Goals"],
  ["report", "Client Summary (AI)"],
];

export default function Advisory() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "burn");
  const [ov, setOv] = useState(null);
  const [error, setError] = useState("");
  const [burnKey, setBurnKey] = useState(0);
  const remount = async () => {
    await reload();
    setBurnKey((k) => k + 1);
  };
  const onPlanned = async () => {
    await reload();
    setBurnKey((k) => k + 1);
    setTab("burn");
    setTimeout(() => document.querySelector('[data-testid="whatif-planner"]')?.scrollIntoView({ behavior: "smooth" }), 400);
  };

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/advisory/overview");
      setOv(r.data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load advisory data");
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div>
      <PageHeader
        title="Advisory"
        subtitle="Cash, tax & debt planning"
        testid="advisory-header"
        actions={ov && <div className="flex flex-wrap items-center gap-2"><ShareVideoButton shared={!!ov.settings.share_with_client} /><ShareToggle on={!!ov.settings.share_with_client} reload={reload} /></div>}
      />
      <PageBody>
        {error && <div className="p-4 mb-4 border text-sm text-[var(--ll-error)]" data-testid="advisory-error">{error}</div>}
        {!ov && !error && (
          <div className="p-16 grid place-items-center" data-testid="advisory-loading"><Loader2 className="animate-spin" /></div>
        )}
        {ov?.alerts?.length > 0 && <AlertBanner alerts={ov.alerts} />}
        {ov && (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="-mx-4 sm:mx-0 overflow-x-auto">
              <TabsList className="bg-[var(--ll-bg-sidebar)] border h-auto p-1 gap-1 inline-flex min-w-max" style={{ borderColor: "var(--ll-border)" }}>
                {TABS.map(([v, label]) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    data-testid={`advisory-tab-${v}`}
                    className="data-[state=active]:bg-white data-[state=active]:text-[var(--ll-ink)] data-[state=active]:shadow-none border border-transparent transition-colors text-white hover:text-white"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <TabsContent value="burn" className="mt-5"><BurnTab key={burnKey} ov={ov} reload={reload} remount={remount} /></TabsContent>
            <TabsContent value="tax" className="mt-5"><TaxTab ov={ov} reload={reload} /></TabsContent>
            <TabsContent value="debt" className="mt-5"><DebtTab ov={ov} reload={reload} /></TabsContent>
            <TabsContent value="goals" className="mt-5"><div className="flex justify-end"><TrainingVideoButton video="goals" /></div><GoalsPanel title="Client goals" testid="staff-goals-panel" onPlanned={onPlanned} /></TabsContent>
            <TabsContent value="report" className="mt-5"><ReportTab /></TabsContent>
          </Tabs>
        )}
      </PageBody>
    </div>
  );
}

function ShareToggle({ on, reload }) {
  const toggle = async (v) => {
    try {
      await api.put("/advisory/settings", { share_with_client: v });
      toast.success(v ? "Clients can now see the Advisory dashboard in their portal" : "Advisory hidden from clients");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };
  return (
    <label className="flex items-center gap-2 text-sm border px-3 py-2 bg-white" style={{ borderColor: "var(--ll-border)" }}>
      <Switch checked={on} onCheckedChange={toggle} data-testid="advisory-share-toggle" />
      <span>Share advisory with client</span>
      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: on ? "var(--ll-success)" : "var(--ll-ink-muted)" }} data-testid="advisory-share-status">{on ? "On" : "Off"}</span>
    </label>
  );
}

function AlertBanner({ alerts }) {
  return (
    <div className="mb-5 p-4 border-l-4" style={{ background: "#FEF2F2", borderColor: "var(--ll-error)" }} data-testid="advisory-alert-banner">
      <div className="flex items-center gap-2 font-medium text-sm text-[var(--ll-error)]"><AlertTriangle size={16} /> Advisory alert</div>
      <ul className="mt-1.5 space-y-1 text-sm">
        {alerts.map((a) => (
          <li key={a.kind} data-testid={`advisory-alert-${a.kind}`}><b>{a.title}.</b> <span className="text-[var(--ll-ink-soft)]">{a.detail}</span></li>
        ))}
      </ul>
      <div className="text-[11px] text-[var(--ll-ink-muted)] mt-2">Owners & admins are emailed when this starts or gets worse; checked every morning and whenever books update.</div>
    </div>
  );
}
